import { type S3Object } from '../../../../drive/types';
import {
  ensureTrailingSlash,
  getDisplayName as getDisplayNameFromPath,
  getParentPath,
  isDirectory,
} from '../../helpers/pathUtils';

export interface BreadcrumbItem {
  text: string;
  key: string;
  onClick?: () => void;
}

export const getDisplayName = (item: S3Object, currentPath?: string): string => {
  const isParentDirectory = item.isParentDirectory;
  const key = item.Key || 'Unknown';

  if (isParentDirectory) return '../';

  if (currentPath && key.startsWith(currentPath)) {
    const normalizedCurrentPath = ensureTrailingSlash(currentPath);
    return key.substring(normalizedCurrentPath.length);
  }

  return getDisplayNameFromPath(key);
};

export const getTargetPath = (item: S3Object, currentPath?: string): string => {
  if (item.isParentDirectory) {
    if (!currentPath) return '';
    return getParentPath(currentPath);
  }
  return item.Key || '';
};

export const getIconName = (item: S3Object): string => {
  if (item.isParentDirectory) return 'ChevronLeftMed';
  return item.Key && isDirectory(item.Key) ? 'FolderHorizontal' : 'FileCode';
};

export const buildBreadcrumbs = (directoryPath: string, onNavigate?: (path: string) => void): BreadcrumbItem[] => {
  const items: BreadcrumbItem[] = [];

  items.push({
    text: 'Drive',
    key: 'root',
    onClick: onNavigate ? () => onNavigate('') : undefined,
  });

  if (directoryPath) {
    const pathParts = directoryPath.split('/').filter(Boolean);
    let buildPath = '';

    pathParts.forEach((part, index) => {
      buildPath += part;
      const isLast = index === pathParts.length - 1;
      const pathForThisItem = buildPath;

      items.push({
        text: part,
        key: pathForThisItem,
        onClick:
          isLast || !onNavigate
            ? undefined
            : () => {
                onNavigate(pathForThisItem);
              },
      });

      buildPath += '/';
    });
  }

  return items;
};

export const getDirectoriesForPath = (allObjects: S3Object[] | undefined, directoryPath: string): S3Object[] => {
  const items: S3Object[] = [];

  if (directoryPath) {
    items.push({
      Key: '../',
      LastModified: undefined,
      Size: undefined,
      isParentDirectory: true,
    });
  }

  const seenDirs = new Set<string>();

  (allObjects || []).forEach((obj) => {
    if (!obj.Key) return;

    const prefix = directoryPath === '' ? '' : ensureTrailingSlash(directoryPath);

    // For objects that start with our prefix, extract parent directories
    if (obj.Key.startsWith(prefix)) {
      const relativePath = obj.Key.substring(prefix.length);

      if (relativePath) {
        // Extract the first directory in the relative path
        const firstSlashIndex = relativePath.indexOf('/');

        if (firstSlashIndex > 0) {
          // This object is in a subdirectory - extract the directory name
          const dirName = relativePath.substring(0, firstSlashIndex);

          if (!seenDirs.has(dirName)) {
            seenDirs.add(dirName);
            // Create a directory marker for this inferred directory
            items.push({
              Key: `${prefix + dirName}/`,
              LastModified: undefined,
              Size: undefined,
            });
          }
        }
      }
    }
  });

  return items;
};
